import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { 
  ClockIcon, 
  EnvelopeIcon, 
  PhoneIcon,
  ChartBarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useResourceStore } from '../store/resourceStore';
import clsx from 'clsx';

// Mock assigned projects data
const mockAssignedProjects = [
  { id: 1, name: 'Identity Federation Hub', hoursPerWeek: 20, startDate: '2023-08-01', endDate: '2023-12-31' },
  { id: 2, name: 'Zero Trust Implementation', hoursPerWeek: 15, startDate: '2023-09-01', endDate: '2024-03-31' }
];

export function ResourceDetail() {
  const { id } = useParams();
  const { getResourceById } = useResourceStore();
  const resource = getResourceById(parseInt(id || ''));

  if (!resource) {
    return <Navigate to="/resources" replace />;
  }

  const utilizationPercentage = Math.min(100, (40 - resource.availableHours) / 40 * 100);

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">{resource.name}</h1>
            <p className="text-gray-400">{resource.title}</p>
          </div>
          <span className={clsx(
            'px-3 py-1.5 text-sm font-medium rounded-full',
            resource.role === 'W2' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
          )}>
            {resource.role}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
            <UserGroupIcon className="h-5 w-5 text-red-500 mr-2" />
            Contact Information
          </h2>
          <div className="space-y-3">
            <p className="flex items-center text-gray-300">
              <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2" />
              {resource.email}
            </p>
            <p className="flex items-center text-gray-300">
              <PhoneIcon className="h-5 w-5 text-gray-400 mr-2" />
              {resource.phone}
            </p>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 text-red-500 mr-2" />
            Availability
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between text-sm text-gray-400 mb-1">
              <span>Current Utilization</span>
              <span>{utilizationPercentage.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2.5">
              <div 
                className={clsx(
                  "h-2.5 rounded-full",
                  utilizationPercentage > 80 ? "bg-red-500" : 
                  utilizationPercentage > 60 ? "bg-yellow-500" : "bg-green-500"
                )}
                style={{ width: `${utilizationPercentage}%` }}
              />
            </div>
            <p className="text-gray-300 mt-2">
              {resource.availableHours} hours available per week
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <ChartBarIcon className="h-5 w-5 text-red-500 mr-2" />
            Current Projects
          </h2>
          <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            Allocate Resource
          </button>
        </div>
        <div className="space-y-4">
          {mockAssignedProjects.map(project => (
            <div key={project.id} className="flex items-center justify-between p-4 bg-gray-750 rounded-lg">
              <div>
                <h3 className="text-white font-medium">{project.name}</h3>
                <p className="text-sm text-gray-400">
                  {project.startDate} - {project.endDate}
                </p>
              </div>
              <div className="text-right">
                <p className="text-white font-medium">{project.hoursPerWeek}h/week</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Skills & Expertise</h2>
        <div className="flex flex-wrap gap-2">
          {resource.skills.map((skill, index) => (
            <span
              key={index}
              className="px-3 py-1.5 text-sm bg-gray-700 text-gray-300 rounded-full"
            >
              {skill}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}